<script setup lang="ts">
import {computed, ref} from 'vue';
import {useRouter} from 'vue-router';
import {getVueLink} from '@/ts/composables/stateful/use-route-params';
import {pagesPath} from '@/ts/router/pages-path';
import SummaryGoals from '@/vue/organisms/summary-goals.vue';
import ReadOnlyView from '@/vue/molecules/read-only/read-only-view.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import SessionUnlocks from '@/vue/molecules/session-summary/session-unlocks.vue';
import {SessionState, type Session, type Intervention} from '@/ts/types/dto/interventions-dto';
import {SessionCompletedScenario} from '@/ts/types/session-completed-scenario';
import HomeworkModal from '@/vue/organisms/homework-modal.vue';
import HomeworkCard from '@/vue/molecules/session-summary/homework-card.vue';

const props = defineProps<{
  scenario: SessionCompletedScenario;
  session: Session;
  intervention: Intervention;
}>();

const router = useRouter();
const showOnlyMyContent = ref<boolean>(false);
const homeWorkOpen = ref(false);

// Get the next session
const nextSession = computed(() => {
  const currentSessionIndex = props.intervention.sessions.findIndex(s => s.id === props.session.id);
  return currentSessionIndex >= 0 && currentSessionIndex < props.intervention.sessions.length - 1
    ? props.intervention.sessions[currentSessionIndex + 1]
    : null;
});

// Check if there's a next session available
const hasNextSession = computed(() => {
  return nextSession.value !== null;
});

// Check if next session is unlocked (homework is optional, does not block)
const isNextSessionUnlocked = computed(() => {
  if (!nextSession.value) {
    return false;
  }
  const {state} = nextSession.value;
  const hasCooldown = nextSession.value.timeUntilUnlockMillis && nextSession.value.timeUntilUnlockMillis > 0;
  return state === SessionState.UNLOCK ||
         state === SessionState.COMPLETED ||
         (state === SessionState.HOMEWORK_PENDING && !hasCooldown);
});

// Specific logic for Next Steps from recap
const nextStepsLink = computed(() => {
  const currentSessionIndex = props.intervention.sessions.findIndex(s => s.id === props.session.id);
  const nextSessionIndex = currentSessionIndex + 1;

  // If there's a next session, go to appropriate page based on its state
  if (nextSessionIndex < props.intervention.sessions.length) {
    const nextSessionData = props.intervention.sessions[nextSessionIndex];

    // If next session is completed, go to its completed page
    // NOTE: Currently, the Continue button is NOT shown when scenario === NEXT_COMPLETED
    // (see showNextUpSection computed property), so this code path is not used.
    // However, it's kept here for defensive programming in case the UI logic changes.
    if (nextSessionData.state === SessionState.COMPLETED) {
      return getVueLink(pagesPath.session.sessionCompletedPage, {
        iid: props.intervention.id,
        sid: nextSessionData.id,
      });
    }

    // Otherwise, go to first page of that session
    return getVueLink(pagesPath.session.sessionPage, {
      iid: props.intervention.id,
      sid: nextSessionData.id,
      pid: 1,
    });
  }

  // If no next session, return to intervention landing
  return getVueLink(pagesPath.intervention.interventionLanding, {
    iid: props.intervention.id,
  });
});

function goToNextSteps(): void {
  void router.push(nextStepsLink.value);
}

// Determine which components to show based on scenario
const showOverview = computed(() => {
  return props.scenario === SessionCompletedScenario.NEXT_PENDING ||
         props.scenario === SessionCompletedScenario.NEXT_COMPLETED;
});

const showGoals = computed(() => {
  return props.scenario === SessionCompletedScenario.NEXT_PENDING ||
         props.scenario === SessionCompletedScenario.NEXT_COMPLETED;
});

// Show the "Next Up" card section when there's any next session
const showNextUpSection = computed(() => {
  const baseCondition = props.scenario === SessionCompletedScenario.JUST_COMPLETED ||
                        props.scenario === SessionCompletedScenario.NEXT_PENDING;
  return baseCondition && hasNextSession.value;
});

// Determine component order
const componentOrder = computed(() => {
  switch (props.scenario) {
    case SessionCompletedScenario.JUST_COMPLETED:
      return ['homework', 'nextUpSection', 'recap'];
    case SessionCompletedScenario.NEXT_PENDING:
      return ['overview', 'goals', 'homework', 'recap', 'nextUpSection'];
    case SessionCompletedScenario.NEXT_COMPLETED:
      return ['homework', 'recap', 'overview', 'goals'];
    default:
      return [];
  }
});
</script>

<template>
  <div class="session-completed-content">
    <template
      v-for="component in componentOrder"
      :key="component"
    >
      <!-- Overview Section -->
      <div v-if="component === 'overview' && showOverview" class="overview-section">
        <h2 class="section-headline">
          {{ $t('summary') }}
        </h2>
        <div v-safe-html="session.description"/>
      </div>

      <!-- Goals Section -->
      <summary-goals
        v-if="component === 'goals' && showGoals && session.goals.length"
        :goals-title="$t('session_goals')"
        :goals="session.goals"
        :finished="true"
      />

      <!-- Homework Section (homework is stored on the next session) -->
      <div v-if="component === 'homework' && nextSession?.homework[0]">
        <homework-modal v-model="homeWorkOpen" :homework-session-id="nextSession!.id"/>
        <homework-card
          :homework="nextSession.homework[0]"
          variant="detail"
          @open-homework="homeWorkOpen = true"
        />
      </div>

      <!-- Recap Section -->
      <div v-if="component === 'recap'" class="recap-section">
        <div class="recap-header">
          <h2 class="recap-title">
            {{ $t('session_recap') }}
          </h2>
          <q-checkbox
            v-model="showOnlyMyContent"
            :label="$t('show_only_my_answers')"
            class="show-only-checkbox"
          />
        </div>

        <div class="recap-content">
          <read-only-view
            :session="session"
            :primary-feelings="intervention.primaryFeelings"
            :thinking-traps="intervention.thinkingTraps"
            :show-only-my-content="showOnlyMyContent"
          />
        </div>
      </div>

      <!-- Next Up Section -->
      <div v-if="component === 'nextUpSection' && showNextUpSection" class="next-up-card">
        <div class="next-up-content">
          <div class="next-up-text">
            <h3 class="next-up-title">
              {{ $t('next_up', {page: nextSession?.name || ''}) }}
            </h3>
            <p v-if="nextSession?.description" v-safe-html="nextSession.description" class="next-up-description"/>
          </div>
          <div class="next-up-action">
            <!-- Show Next Steps Button if unlocked -->
            <balm-btn
              v-if="isNextSessionUnlocked"
              class="next-steps-btn"
              :label="$t('continue')"
              theme="outline"
              @click="goToNextSteps"
            />
            <!-- Show Session Unlock Info if locked due to cooldown -->
            <session-unlocks v-else :session="nextSession!"/>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.session-completed-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-5);
  width: 100%;
}


.section-headline {
  @include font-h3-bold;
  margin-bottom: var(--Spacing-spacing-4);
}

.overview-section {
  margin: var(--Spacing-spacing-7) var(--Spacing-spacing-8) var(--Spacing-spacing-8) var(--Spacing-spacing-6);
}

.recap-section {
  display: flex;
  flex-direction: column;
  padding-top: var(--Spacing-spacing-4);
  gap: var(--Spacing-spacing-2);
}

.recap-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--Spacing-spacing-3);
}

.recap-title {
  @include font-h3-bold;
  margin: 0;
}

.show-only-checkbox {
  flex-shrink: 0;
}


.recap-content {
  @include q-card-white;
}

.next-up-card {
  @include q-card-blue;
  padding: var(--Spacing-spacing-5);

  @include screen-lg {
    padding: var(--Spacing-spacing-7);
  }
}

.next-up-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: var(--Spacing-spacing-6);

  @media screen and (max-width: 639px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--Spacing-spacing-4);
  }
}

.next-up-text {
  flex: 1;
}

.next-up-title {
  @include font-h3-bold;
  margin: 0 0 var(--Spacing-spacing-2) 0;
  color: var(--Text-text-90);
}

.next-up-description {
  @include font-body;
  margin: 0;
  color: var(--Text-text-70);
}

.next-up-action {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;

  @media screen and (max-width: 639px) {
    justify-content: flex-start;
  }
}

.next-steps-btn {
  color: var(--Text-text-90) !important;
}

</style>